import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { size as _size } from 'lodash';
import { i18nIntl, formatNumber, adaptGameToContainer, getStageName, isNotEmpty, isDesktop } from '../../../lib/ep.js';
import { isMultiStage, stage_categories, stage_promotion_types as spt } from '../../../data/bootstrap_stages.js';

import { SquaredButton } from '../../builder/ActionButton';
import Tooltip from '../../builder/Tooltip';

import FloatingLayerPublic from '../../front/FloatingLayerPublic';
import QuizStatsPublic from '../../front/quiz/QuizStatsPublic';
import { UserTeamParticipationDetail } from '../../../containers/TeamClient.js';
import { TicketLayer } from './UserParticipationsMultistageList.js';
import PickFavoriteFinal from '../PickFavoriteFinal.js';
import BattleFinal from '../BattleFinal.js';
import ButtonCollection from '../../builder/ButtonCollection.js';
import LikeDislikeFinal from '../LikeDislikeFinal.js';
import LeveledHTMLText from '../helpers/LeveledHTMLText.js';
import { GuessScoreResumeWithPoints } from '../GuessScoreResume.js';
import OneTimeBracketFinal from '../OneTimeBracketFinal.js';

const max_paricipations_to_show = 50;

const StageTitle = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const st = props?.stage?.Stage ?? props?.stage; 
	return (
		<p class="gui_not_in_bottom_small">{`${getStageName(st ?? {},i18n)}`}</p>
	)
}

const UserParticipationsList = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ max_score_in_stage, setMaxScoreInStage ] = useState(0);
	const [ display_participation_details, setDisplayParticipationDetails ] = useState(false);
	const [ display_team_details, setDisplayTeamDetails ] = useState(false);
	const [ display_ticket_details, setDisplayTicketDetails ] = useState(false);
	const [ display_faceoff_details, setDisplayFaceoffDetails ] = useState(false);
	const [ display_guess_score_details, setDisplayGuessScoreDetails ] = useState(false);

	const whole_in_layer = props?.in_layer ?? false;

	const stage = props.stage;

	const participations = useRef(props?.participations ?? []);
	const best_participation_indicator = 'score';
	const best_participation_direction = 1;	// look for the highest value

	const is_builder = props?.is_builder ?? false;
	const is_game = props?.is_game ?? false;
	const is_quiz = props?.is_quiz ?? false;
	const is_team = props?.is_team ?? false;
	const display_public_stats = props?.display_public_stats ?? false;
	const promotion = props?.promotion ?? false;
	const tz = promotion.timezone;

	const bold_text_class = (!is_builder) ? 'hb_mainFontWeightBold' : 'gui_force_bold';
	const color_text_class = (!is_builder) ? 'hb_mainColorText' : 'blueColor';
	const secondary_font_text_class = (!is_builder) ? 'hb_secondaryFont' : '';

	useEffect(() => {
		setMaxScoreInStage(calculateMaxValuePerStage(participations.current));
	}, []);

	/**
	 * Returns the TOP value among all the participations in the current stage.
	 *
	 * @param  {array}  participations_in_stage group of participations in the current stage
	 * @param  {string} field                   field name within "User" object
	 * @param  {number} direction               set the ordering top definition ("1" for definining the highest value as TOP / "-1" for defining the lowest value as TOP)
	 * @return {number}
	 */
	const calculateMaxValuePerStage = (participations_in_stage, field, direction) => {
		let return_value = 0;

		if(participations_in_stage.length < 1) return return_value;

		if(field == undefined) field = best_participation_indicator;
		if(direction == undefined) direction = best_participation_direction;

		if(direction < 0) return_value = participations_in_stage[0][field];

		participations_in_stage.map((participation) => {
			const participation_value = parseFloat(participation[field]);

			if((direction > 0 && participation_value > return_value) || (direction < 0 && participation_value < return_value)) {
				return_value = participation_value;
			}
		});

		return return_value;
	};

	const displayTicket = (participation) => {
		setDisplayTicketDetails(participation);
	};

	const displayDetails = (participation, _is_team) => {
		const _st = (props?.stage?.Stage ?? props?.stage)  ?? (props?.stages ?? []).find((s) => (s.Stage.id == participation.stage))?.Stage ?? {};
		_is_team = _is_team == undefined ? is_team : _is_team;
		if(_is_team) {
			setDisplayTeamDetails({
				...participation.quiz_entry,
				created:participation.created,
				score:participation.score,
				promotion,
				stage:_st,
				user_id:participation.id,
				locale:participation.locale,
				intl:props.intl,
			});

			return;
		}

		if([spt.FACEOFF.id, spt.LIKEDISLIKE.id, spt.PICK_FAVORITE.id].includes(parseInt(_st.promotion_type_id))) {
			setDisplayFaceoffDetails({
				user:{ ...participation },
				user_answer:participation.quiz_entry,
				promotion,
				stage:{
					..._st,
					final_message:" ",
					faceoff_display_stats:false,
					likedislike_display_stats:false,
				},
				locale:props.locale,
				intl:props.intl,
				adaptToContainer:adaptGameToContainer
			});
			return;
		}

		if([spt.SCORE_PREDICTOR.id, spt.BRACKET_PREDICTOR.id].includes(parseInt(_st.promotion_type_id))) {
			setDisplayGuessScoreDetails({
				user:{ ...participation },
				created:participation.created,
				promotion,
				stage:_st,
				teams:promotion.guess_score_teams,
				results:_st?.guess_score_matches_result ?? {},
				answers:participation?.quiz_entry?.guess_score ?? {},
				user_id:participation.id,
				locale:props.locale,
				intl:props.intl,
			});

			return;
		}

		setDisplayParticipationDetails(participation);

		if(props.on_view_detail != undefined) {
			const n_title = EPDateFormat(participation.created, 'L, LT');
			props.on_view_detail(n_title, <SquaredButton
				onClick={() => closeDetails()}
				icon='knk-arrow-l-big'
				_class={{ variable_icon_color:true }}
				aria-label="Close details"
			/>);
		}
	};

	const closeDetails = () => {
		setDisplayParticipationDetails(false);
		if(props.on_view_detail != undefined) props.on_view_detail(false, false);
	};

	let previous_date = '0000-00-00 00:00:00';
	const has_code_restriction = (props.stage != undefined && props.stage.reqs_type != undefined && props.stage.reqs_type == 1) ? true : false;
	const has_tickets = participations.current.filter((p) => { 
		const stage_ticket = p.extra?.stage_ticket ?? false;
		return stage_ticket?.graphic_info != undefined && stage_ticket?.graphic_info!= null && stage_ticket?.graphic_info != '';
	}).length > 0;

	if(is_quiz && display_participation_details !== false && whole_in_layer && props.stage) {
		return (
			<QuizStatsPublic
				promotion_id={promotion.id}
				user_id={display_participation_details.id}
				stage_id={props.stage.id}
			/>
		)
	}

	const has_score = (is_game || is_quiz || is_team) ? true : false;
	const has_quiz = (is_quiz && display_public_stats) ? true : false;

	const total_participations = props?.total_participations ?? participations.current.length;
	const has_more = (total_participations > max_paricipations_to_show) ? true : false;

	let num_cols = 4;
	if(has_code_restriction) num_cols++;
	if(has_score) num_cols++;
	if(has_quiz || is_team) num_cols++;
	if(is_team) num_cols++;
	if(has_tickets) num_cols++;

	if(total_participations == 0) {
		return (
			<div class={'gui_participations_list' + ((is_builder) ? ' is_builder' : '')}>
				<p>{i18n("You haven't completed any entries yet.")}</p>
			</div>
		)
	}
	const time_played = display_participation_details?.StageScore?.time ?? display_participation_details?.quiz_entry?.time ?? 0;
	return (
		<div class={'gui_participations_list ' + ((!is_builder) ? 'gui_text_block' : 'is_builder')}>
			<table class="single_user_participations" cellSpacing="0" cellPadding="0" style={{width:'100%'}}>
				<thead>
					<tr>
						<th class={secondary_font_text_class}>#</th>

						<th />

						<th class={'gui_text_align_start ' + secondary_font_text_class}>{i18n("Date")}</th>

						<th class={'gui_text_align_center ' + secondary_font_text_class}>{i18n("time", 'ucfirst')}</th>

						{(has_code_restriction) && (
							<th class={'gui_text_align_center ' + secondary_font_text_class}>{i18n("Code")}</th>
						)}

						{(has_score) && (
							<th class={'gui_text_align_end ' + secondary_font_text_class}>{i18n("Score")}</th>
						)}

						{(has_quiz) && (
							<th class={'gui_text_align_end ' + secondary_font_text_class}>{i18n("Answers")}</th>
						)}

						{(is_team) && (
							<th class={'gui_text_align_end ' + secondary_font_text_class}>{i18n("View")}</th>
						)}

						{(has_tickets || is_game) && (
							<th class={'gui_text_align_end ' + secondary_font_text_class}>&nbsp;</th>
						)}
					</tr>
				</thead>

				<tbody>
					{(participations.current).map((part, i) => {
						const is_same_date = (i == 0 || EPDateFormat(previous_date, 'LL') == EPDateFormat(part.created, 'LL')) ? true : false;
						previous_date = part.created;
						const absolute_counter = total_participations - i;
						const counter = (absolute_counter < 10) ? `0${absolute_counter}` : absolute_counter;
						const is_max = (max_score_in_stage > 0 && max_score_in_stage == part[best_participation_indicator]) ? true : false;

						let tr_class = [];
						if(is_max) tr_class.push(color_text_class);
						if(!is_same_date) tr_class.push('date_change');

						const has_prize = part?.has_prize ?? false;
						const show_ticket = part?.extra?.stage_ticket?.graphic_info ?? false;
						
						let is_team_stage = is_team;
						let is_bracket_stage = has_quiz;
						let is_game_stage = is_game;
						let is_quiz_stage = has_quiz;
						
						let title_stage = null;
						const _stage = (props.stages).find((st) => part.stage == st.Stage.id);
						const is_pick_favorite = _stage.Stage.promotion_type_id == spt.PICK_FAVORITE.id ? true : false;
						const is_guess_score = ([spt.SCORE_PREDICTOR.id].includes(parseInt(_stage.Stage.promotion_type_id))) ? true : false;
						const is_bracket_predictor = ([spt.BRACKET_PREDICTOR.id].includes(parseInt(_stage.Stage.promotion_type_id))) ? true : false;
						if(_stage && isMultiStage(promotion.promotion_type_id)){
							is_team_stage = _stage.Stage.type == stage_categories.ID_TEAM_CATEGORY.id;
							is_bracket_stage = _stage.Stage.type == stage_categories.ID_BRACKET_CATEGORY.id;
							is_quiz_stage = _stage.Stage.type == stage_categories.ID_QUIZ_CATEGORY.id || _stage.Stage.type == stage_categories.ID_TIMED_CATEGORY.id;
							title_stage = getStageName(_stage.Stage,i18n);
						}
						return (
							<tr key={'p' + i} class={tr_class.join(" ")}>
								<td class={'ranking_pos' + ((is_max) ? ' ' + bold_text_class : '')}>{counter}</td>

								<td class="has_prize">
									{(has_prize) && (
										<div class="gui_pos_rel gui_inline_block gui_small_icon knk-winners">
											<Tooltip with_icon={false}>You've won a prize</Tooltip>
										</div>
									)}
								</td>

								<td class={'gui_text_align_start' + ((is_max) ? ' ' + bold_text_class : '')}>
									{moment.tz(part.created, 'Europe/Madrid').tz(tz).format((!isDesktop()) ? 'll' : 'll')}	
								</td>

								<td class={'gui_text_align_center' + ((is_max) ? ' ' + bold_text_class : '')}>
									{moment.tz(part.created, 'Europe/Madrid').tz(tz).format('LT')}	
								</td>

								{(has_code_restriction) && (
									<td class={'gui_text_align_center' + ((is_max) ? ' ' + bold_text_class : '')}>{(part.code != '' && part.code != null) ? part.code : "-"}</td>
								)}

								{(has_score) && (
									<td class={'gui_text_align_end ' + bold_text_class}>
										<span class="gui_flexbox_auto gui_flex_end gui_flex_align_center">
											{(best_participation_indicator == 'score' && is_max) && (
												<span class="top_icon knk-top-item gui_not_in_end_small gui_pos_rel" style={{top:'-2px'}} />
											)}
											<span>{formatNumber(part.score)}</span>
										</span>
									</td>
								)}

								{(is_quiz_stage || is_team_stage || is_bracket_stage || is_pick_favorite || is_guess_score || is_bracket_predictor) && (
									<td class={'gui_text_align_end ' + bold_text_class} style={{display:'flex', justifyContent:'flex-end'}}>
										<span class="gui_flexbox_auto gui_flex_end gui_flex_align_center">
											<ButtonCollection _class={{other:'gui_flex_end'}}>
												{(part.quiz_entry) ? (
													<SquaredButton
														onClick={() => part.quiz_entry && ((is_team_stage || is_pick_favorite) ? displayDetails(part,is_team_stage) : setDisplayParticipationDetails(part))}
														icon={'knk-visible'}
														_class={{variable_icon_color:true}}
														aria-label="View details"
													/>
												): (
													<SquaredButton 
														icon={false}
														color="transparent"
														_class={{other:'gui_withoutEvents'}}
													/>
												)}
											</ButtonCollection>
										</span>
									</td>
								)}

								{(has_tickets && show_ticket) && (
									<td class={'gui_text_align_end ' + bold_text_class} style={{display:'flex', justifyContent:'flex-end'}}>
										<ButtonCollection _class={{other:'gui_flex_end'}}>
											<SquaredButton
												icon='knk-buy-ticket'
												onClick={() => displayTicket(part)}
												_class={{ other:`original_color_item`, variable_icon_color:true }}
												aria-label="View ticket"
											/>
										</ButtonCollection>
									</td>
								)}
								
								{(is_game_stage && !is_quiz_stage && !is_team_stage && !is_bracket_stage && !is_pick_favorite && !is_guess_score && !is_bracket_predictor) && (
									<td class={'gui_text_align_end ' + bold_text_class} style={{display:'flex', justifyContent:'flex-end'}}>
										<ButtonCollection _class={{other:'gui_flex_end'}}>
											<SquaredButton
												icon='knk-visible'
												onClick={() => displayDetails(part)}
												_class={{ other:`original_color_item`, variable_icon_color:true }}
												aria-label="View participation"
											/>
										</ButtonCollection>
									</td>
								)}
							</tr>
						)
					})}
					{(has_more) && (
						<tr>
							<td colSpan={num_cols} class="blank_row">
								<FormattedMessage
									id="Only the last {num} participations are shown."
									values={{ num:max_paricipations_to_show }}
								/>
							</td>
						</tr>
					)}
				</tbody>
			</table>

			{(display_ticket_details) && (
				<TicketLayer
					participation={display_ticket_details}
					promotion={promotion}
					stage={props.stage}
					backButton={() => setDisplayTicketDetails(false)}
					intl={props.intl}
					locale={props.locale}
				/>
			)}

			{(display_participation_details !== false) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${i18n("Participation detail")}</h1><p>${EPDateFormat(display_participation_details.created, 'LL, LT')}</p>`}
					backButton={() => setDisplayParticipationDetails(false)}
				>	
					{(isMultiStage(promotion.promotion_type_id)) && (
						<StageTitle 
							stage={display_participation_details?.stage_data ?? null}
							intl = {props.intl}
						/>
					)}

					{(isNotEmpty(display_participation_details, 'time') || 	isNotEmpty(display_participation_details, 'score')) && (
						<LeveledHTMLText
							_class={{ other:'gui_participations_list'}}
							//style={{ minHeight:'100px' }}
						>
							<section class="gui_quiz_public_stats" style={{marginBottom:'2em'}}>
								<article class="gui_flexbox_auto gui_flex_align_center">

									{(isNotEmpty(display_participation_details, 'time')) && (
										<p class="question_text">
											{i18n("Played time")}: <strong>&nbsp;{time_played} {i18n("sec")}</strong>
										</p>
									)}

									{(isNotEmpty(display_participation_details, 'score')) && (
										<p class="question_text">
											{i18n("Score")}: <strong>&nbsp;{display_participation_details.score} {i18n("points")}</strong>
										</p>
									)}
									
								</article>
							</section>
						</LeveledHTMLText>
					)}

					{(!display_participation_details?.stage_data?.is_game) && (
						<QuizStatsPublic
							promotion_id={promotion.id}
							user_id={display_participation_details.id}
							stage_id={display_participation_details?.stage ?? (display_participation_details?.stage_data?.id ?? null)}
						/>
					)}

				</FloatingLayerPublic>
			)}

			{(display_team_details !== false) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${props.intl.formatMessage({ id: "The team of {name}" }, { name:props.username })}</h1>`}
					backButton={() => setDisplayTeamDetails(false)}
				>	
					{(isMultiStage(promotion.promotion_type_id)) && (
						<StageTitle 
							stage={display_team_details?.stage ?? null}
							intl = {props.intl}
						/>
					)}

					<div class="in_user_list">
						<UserTeamParticipationDetail
							{...display_team_details}
						/>
					</div>
				</FloatingLayerPublic>
			)}

			{(display_faceoff_details) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${props.intl.formatMessage({ id: "The choice of {name}" }, { name:props.username })}</h1>`}
					backButton={() => setDisplayFaceoffDetails(false)}
				>
					{(isNotEmpty(display_faceoff_details.user, 'score')) && (
						<LeveledHTMLText
							_class={{ other:'gui_participations_list'}}
						>
							<section class="gui_quiz_public_stats">
								<div class="gui_flexbox_auto">
									{i18n("Score")}: <strong class="gui_not_in_start_micro">{display_faceoff_details.user.score} {i18n("points")}</strong>
								</div>
							</section>

						</LeveledHTMLText>
					)}

					<div class="in_user_list">
						{(display_faceoff_details.stage.promotion_type_id == spt.FACEOFF.id) && (
							<BattleFinal
								{...display_faceoff_details}
							/>
						)}

						{(display_faceoff_details.stage.promotion_type_id == spt.PICK_FAVORITE.id) && (
							<PickFavoriteFinal
								in_ranking={true}
								{...display_faceoff_details}
							/>
						)}

						{(display_faceoff_details.stage.promotion_type_id == spt.LIKEDISLIKE.id) && (
							<LikeDislikeFinal
								{...display_faceoff_details}
							/>
						)}
					</div>
				</FloatingLayerPublic>
			)}
			{(display_guess_score_details !== false) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${props.intl.formatMessage({ id: "The prediction of {name}" }, { name:props.username })}</h1>`}
					backButton={() => setDisplayGuessScoreDetails(false)}
				>
					{(isNotEmpty(display_guess_score_details.user, 'score')) && (
						<LeveledHTMLText
							_class={{ other:'gui_participations_list'}}
						>
							<section class="gui_quiz_public_stats">
								<div class="gui_flexbox_auto">
									{i18n("Score")}: <strong class="gui_not_in_start_micro">{display_guess_score_details.user.score} {i18n("points")}</strong>
								</div>
							</section>

						</LeveledHTMLText>
					)}
					<div class="in_user_list gui_game_wrapper">
						{(display_guess_score_details.stage.promotion_type_id == spt.BRACKET_PREDICTOR.id) && (
							<OneTimeBracketFinal
								{...display_guess_score_details}
								is_standalone={true}
								only_contents={true}
							/>
						)}

						{(display_guess_score_details.stage.promotion_type_id == spt.SCORE_PREDICTOR.id) && (
							<GuessScoreResumeWithPoints
								{...display_guess_score_details}
							/>
						)}
					</div>
						
				</FloatingLayerPublic>
			)}
		</div>
	);
}

export default injectIntl(UserParticipationsList);